/*
==============================================================================
Rcrea | Media con texto Güten Brew
- Play inline estable
- Portada visible hasta que el video realmente entrega un primer frame
- Pause al hover/click
- Volumen
- Modal fullscreen
- Loop infinito sin botoneras, con fallback de portada si autoplay falla
==============================================================================
*/

(function () {
  const SELECTOR = '[data-gb-media-feature]';

  function safePlay(video) {
    if (!video) return Promise.reject(new Error('Video no encontrado'));

    try {
      const promise = video.play();

      if (promise && typeof promise.then === 'function') {
        return promise;
      }

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }

  function safePause(video) {
    if (!video) return;

    try {
      video.pause();
    } catch (error) {}
  }

  function initSection(section) {
    if (!section || section.dataset.gbMfInitialized === 'true') return;

    section.dataset.gbMfInitialized = 'true';

    const frame = section.querySelector('[data-gb-mf-frame]');
    const playButton = section.querySelector('[data-gb-mf-play]');
    const pauseButton = section.querySelector('[data-gb-mf-pause]');
    const soundButton = section.querySelector('[data-gb-mf-sound]');
    const expandButton = section.querySelector('[data-gb-mf-expand]');
    const modal = section.querySelector('[data-gb-mf-modal]');
    const closeButtons = section.querySelectorAll('[data-gb-mf-close]');
    const inlineVideo = section.querySelector('.gb-media-feature__video');
    const modalVideo = section.querySelector('.gb-media-feature__modal-video');
    const isLoopMode = section.dataset.gbMfLoopMode === 'true';

    let observer = null;
    let firstFrameRequested = false;

    function setSoundState(isSoundOn) {
      if (!soundButton) return;

      soundButton.classList.toggle('is-sound-on', isSoundOn);
      soundButton.setAttribute('aria-label', isSoundOn ? 'Silenciar video' : 'Activar sonido');
    }

    function setLoadingState(isLoading) {
      section.classList.toggle('is-loading', isLoading);
    }

    function setPlayingState(isPlaying) {
      section.classList.toggle('is-playing', isPlaying);
      if (isPlaying) section.classList.remove('is-loading');
    }

    function setVideoReadyState(isReady) {
      section.classList.toggle('is-video-ready', isReady);
    }

    function markVideoReady() {
      if (!inlineVideo) return;

      /*
        Este estado es el que oculta la portada.
        No se activa sólo con play/canplay; se activa cuando ya hay frame disponible.
      */
      setVideoReadyState(true);
      setPlayingState(!inlineVideo.paused || isLoopMode);
    }

    function requestFirstFrameReady() {
      if (!inlineVideo || firstFrameRequested) return;

      firstFrameRequested = true;

      if (inlineVideo.readyState >= 2) {
        window.requestAnimationFrame(markVideoReady);
        return;
      }

      if (typeof inlineVideo.requestVideoFrameCallback === 'function') {
        try {
          inlineVideo.requestVideoFrameCallback(function () {
            markVideoReady();
          });
          return;
        } catch (error) {}
      }

      const fallbackTimer = window.setTimeout(function () {
        if (inlineVideo.readyState >= 2 && (!inlineVideo.paused || isLoopMode)) {
          markVideoReady();
        }
      }, 220);

      inlineVideo.addEventListener(
        'loadeddata',
        function () {
          window.clearTimeout(fallbackTimer);
          if (!inlineVideo.paused || isLoopMode || section.classList.contains('is-loading')) {
            markVideoReady();
          }
        },
        { once: true }
      );
    }

    function prepareInlineVideo() {
      if (!inlineVideo) return;

      inlineVideo.setAttribute('playsinline', '');
      inlineVideo.setAttribute('webkit-playsinline', '');
      inlineVideo.setAttribute('preload', 'metadata');

      if (isLoopMode) {
        inlineVideo.muted = true;
        inlineVideo.defaultMuted = true;
        inlineVideo.volume = 0;
        inlineVideo.loop = true;
        inlineVideo.setAttribute('muted', '');
        inlineVideo.setAttribute('loop', '');
        inlineVideo.setAttribute('autoplay', '');
      }
    }

    function playInlineVideo() {
      if (!inlineVideo) return;

      prepareInlineVideo();

      firstFrameRequested = false;

      if (!isLoopMode) {
        inlineVideo.muted = true;
        inlineVideo.defaultMuted = true;
        inlineVideo.volume = 1;
        setLoadingState(true);
      } else {
        inlineVideo.muted = true;
        inlineVideo.defaultMuted = true;
        inlineVideo.volume = 0;
      }

      if (inlineVideo.readyState === 0) {
        try {
          inlineVideo.load();
        } catch (error) {}
      }

      safePlay(inlineVideo)
        .then(function () {
          requestFirstFrameReady();
        })
        .catch(function () {
          setLoadingState(false);

          /*
            Si autoplay falla en loop, NO ocultamos la portada.
            Así evitamos el cuadrado verde en preview/incógnito.
          */
          if (!isLoopMode) {
            setPlayingState(false);
            setVideoReadyState(false);
          }
        });

      setSoundState(false);
    }

    function pauseInlineVideo() {
      if (!inlineVideo || isLoopMode) return;

      safePause(inlineVideo);
      setPlayingState(false);
      setLoadingState(false);
      setVideoReadyState(false);
      setSoundState(false);
    }

    function toggleSound() {
      if (!inlineVideo || isLoopMode) return;

      inlineVideo.muted = !inlineVideo.muted;

      if (!inlineVideo.muted) {
        inlineVideo.volume = 1;
        safePlay(inlineVideo).catch(function () {});
      }

      setSoundState(!inlineVideo.muted);
    }

    function openModal() {
      if (!modal || isLoopMode) return;

      modal.classList.add('is-active');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('gb-mf-modal-open');
      document.body.classList.add('gb-mf-modal-open');

      if (modalVideo) {
        if (inlineVideo) {
          try {
            modalVideo.currentTime = inlineVideo.currentTime || 0;
          } catch (error) {}
        }

        modalVideo.muted = false;
        modalVideo.defaultMuted = false;
        modalVideo.volume = 1;

        if (modalVideo.readyState === 0) {
          try {
            modalVideo.load();
          } catch (error) {}
        }

        safePlay(modalVideo).catch(function () {});
      }

      safePause(inlineVideo);
    }

    function closeModal() {
      if (!modal) return;

      modal.classList.remove('is-active');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('gb-mf-modal-open');
      document.body.classList.remove('gb-mf-modal-open');

      if (modalVideo) {
        safePause(modalVideo);

        if (inlineVideo) {
          try {
            inlineVideo.currentTime = modalVideo.currentTime || inlineVideo.currentTime || 0;
          } catch (error) {}
        }
      }
    }

    prepareInlineVideo();

    if (inlineVideo) {
      inlineVideo.addEventListener('loadeddata', function () {
        if (isLoopMode || section.classList.contains('is-loading') || !inlineVideo.paused) {
          markVideoReady();
        }
      });

      inlineVideo.addEventListener('playing', function () {
        requestFirstFrameReady();
      });

      inlineVideo.addEventListener('timeupdate', function () {
        if (inlineVideo.currentTime > 0.02 && (isLoopMode || section.classList.contains('is-loading') || section.classList.contains('is-playing'))) {
          markVideoReady();
        }
      });

      inlineVideo.addEventListener('pause', function () {
        if (isLoopMode) {
          setPlayingState(false);
          return;
        }

        if (!inlineVideo.ended) return;

        setPlayingState(false);
        setVideoReadyState(false);
      });

      inlineVideo.addEventListener('ended', function () {
        if (!inlineVideo.loop && !isLoopMode) {
          setPlayingState(false);
          setVideoReadyState(false);
          setSoundState(false);
        }
      });

      inlineVideo.addEventListener('error', function () {
        setLoadingState(false);
        setPlayingState(false);
        setVideoReadyState(false);
      });

      if (isLoopMode) {
        window.requestAnimationFrame(function () {
          playInlineVideo();
        });

        if ('IntersectionObserver' in window) {
          observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting && entry.intersectionRatio > 0.18) {
                  playInlineVideo();
                } else {
                  safePause(inlineVideo);
                  setPlayingState(false);
                }
              });
            },
            {
              threshold: [0, 0.18, 0.6]
            }
          );

          observer.observe(inlineVideo);
        }
      }
    }

    if (playButton) {
      playButton.addEventListener('click', function (event) {
        event.preventDefault();
        playInlineVideo();
      });
    }

    if (pauseButton) {
      pauseButton.addEventListener('click', function (event) {
        event.preventDefault();
        pauseInlineVideo();
      });
    }

    if (frame && inlineVideo && !isLoopMode) {
      frame.addEventListener('click', function (event) {
        const clickedControl = event.target.closest('button, a');

        if (clickedControl) return;

        if (!section.classList.contains('is-playing')) {
          playInlineVideo();
        }
      });
    }

    if (soundButton) {
      soundButton.addEventListener('click', function (event) {
        event.preventDefault();

        if (isLoopMode) return;

        if (!section.classList.contains('is-playing')) {
          playInlineVideo();
          return;
        }

        toggleSound();
      });
    }

    if (expandButton) {
      expandButton.addEventListener('click', function (event) {
        event.preventDefault();
        openModal();
      });
    }

    closeButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        closeModal();
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && modal && modal.classList.contains('is-active')) {
        closeModal();
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (!inlineVideo) return;

      if (document.hidden) {
        safePause(inlineVideo);
        if (isLoopMode) setPlayingState(false);
      } else if (isLoopMode) {
        playInlineVideo();
      }
    });
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    let section = null;

    if (event.target.matches && event.target.matches(SELECTOR)) {
      section = event.target;
    } else {
      section = event.target.querySelector(SELECTOR);
    }

    if (section) {
      section.dataset.gbMfInitialized = 'false';
      initSection(section);
    }
  });
})();
